/**
 * Session Timeout Manager
 * Manages user session duration and automatic logout
 */

export interface SessionTimeoutConfig {
  durationMinutes: number; // 0 = never expire
  onTimeout: () => void;
  warningMinutes?: number; // Optional warning before timeout
  onWarning?: () => void;
}

class SessionTimeoutManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningId: NodeJS.Timeout | null = null;
  private config: SessionTimeoutConfig | null = null;
  private sessionStartTime: number | null = null;

  /**
   * Start session timeout
   */
  start(config: SessionTimeoutConfig): void {
    this.stop(); // Clear any existing timers

    this.config = config;
    this.sessionStartTime = Date.now();

    // Don't set timeout if duration is 0 (never expire)
    if (config.durationMinutes === 0) {
      return;
    }

    const durationMs = config.durationMinutes * 60 * 1000;

    // Set warning timer if configured
    if (config.warningMinutes && config.onWarning) {
      const warningMs =
        (config.durationMinutes - config.warningMinutes) * 60 * 1000;
      if (warningMs > 0) {
        this.warningId = setTimeout(() => {
          config.onWarning?.();
        }, warningMs);
      }
    }

    // Set timeout timer
    this.timeoutId = setTimeout(() => {
      this.handleTimeout();
    }, durationMs);
  }

  /**
   * Stop session timeout
   */
  stop(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningId) {
      clearTimeout(this.warningId);
      this.warningId = null;
    }
    this.config = null;
    this.sessionStartTime = null;
  }

  /**
   * Reset session timer (e.g., on user activity)
   */
  reset(): void {
    if (this.config) {
      this.start(this.config);
    }
  }

  /**
   * Handle session timeout
   */
  private handleTimeout(): void {
    if (this.config?.onTimeout) {
      this.config.onTimeout();
    }
    this.stop();
  }

  /**
   * Get remaining session time in milliseconds
   */
  getRemainingTime(): number | null {
    if (
      !this.sessionStartTime ||
      !this.config ||
      this.config.durationMinutes === 0
    ) {
      return null;
    }

    const elapsed = Date.now() - this.sessionStartTime;
    const total = this.config.durationMinutes * 60 * 1000;
    return Math.max(0, total - elapsed);
  }

  /**
   * Check if session is active
   */
  isActive(): boolean {
    return this.timeoutId !== null || this.config?.durationMinutes === 0;
  }
}

export const sessionTimeoutManager = new SessionTimeoutManager();
